import { createTheme, alpha as fade } from '@mui/material/styles';

import {
  BLACK_BLUE,
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  DARK_BLUE,
  DOCUMENT,
  FILE,
  FORM,
  GREEN,
  GREEN_LIGHT,
  GREY,
  GREY_BLUE,
  GREY_DARK,
  GREY_DARKER,
  GREY_LIGHT,
  GREY_LIGHTER,
  OBSERVATION,
  ORANGE,
  ORANGE_LIGHT,
  PRIMARY_COLOR,
  PRIMARY_DARKMODE_COLOR,
  PURPLE,
  PURPLE_DARK,
  PURPLE_LIGHT,
  RED,
  RED_DARK,
  RED_LIGHT,
  VIOLET_LIGHT,
  WHITE,
  YELLOW,
  YELLOW_DARK,
  YELLOW_LIGHT,
} from 'theme/colors';
import {
  ARRAY_TYPO,
  CLASSIC_TYPO,
  FONTSIZE_L,
  FONTSIZE_M,
  FONTSIZE_S,
  FONTSIZE_XL,
  FONTSIZE_XS,
  MEDIUM_TYPO,
} from 'theme/typography';
import { colorTheme } from 'utils/constants';

const paletteDarkTheme = {
  text: {
    primary: GREY_LIGHT,
    secondary: GREY,
    tertiary: GREY_DARK,
    quaternary: GREY_DARKER,
    disabled: GREY_DARK,
    contrast: DARK_BLUE,
    info: BLUE,
    white: WHITE,
  },
  background: {
    tertiary: BLACK_BLUE,
    primary: DARK_BLUE,
    secondary: GREY_BLUE,
    dark: GREY_LIGHT,
  },
  icon: {
    contrast: '#000',
  },
};

const paperDarkTheme = {
  backgroundColor: BLACK_BLUE,
  backgroundImage: 'none',
};

export const linkStyle = {
  fontFamily: CLASSIC_TYPO,
  fontSize: `${FONTSIZE_S} !important`,
  lineHeight: '1.5',
  fontWeight: 'normal',
  color: BLUE,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: BLUE_DARK,
  },
  '&:focus-visible': {
    outline: `2px solid ${BLUE}`,
  },
  '&:active': {
    color: DARK_BLUE,
  },
};

const theme = (mode = colorTheme.light) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: DARK_BLUE, // ne pas enlever pour MUI
      },
      secondary: {
        light: ORANGE_LIGHT,
        main: PRIMARY_COLOR,
        darkMode: PRIMARY_DARKMODE_COLOR,
      },
      error: {
        light: RED_LIGHT,
        main: RED,
        dark: RED_DARK,
      },
      limitation: {
        light: ORANGE_LIGHT,
        main: ORANGE,
        dark: ORANGE,
      },
      warning: {
        light: YELLOW_LIGHT,
        main: YELLOW,
        dark: YELLOW_DARK,
      },
      info: {
        light: BLUE_LIGHT,
        main: BLUE,
        mainLight: `${BLUE}19`,
      },
      lab: {
        light: PURPLE_LIGHT,
        main: PURPLE,
        dark: PURPLE_DARK,
      },
      draft: {
        light: ORANGE_LIGHT,
        main: ORANGE,
      },
      success: {
        light: GREEN_LIGHT,
        main: GREEN,
      },
      text: {
        white: WHITE,
        primary: GREY_DARK,
        secondary: GREY,
        tertiary: GREY_LIGHT,
        dark: DARK_BLUE,
        info: BLUE,
        main: PRIMARY_COLOR,
        warning: ORANGE,
        error: RED,
        disabled: GREY_LIGHTER,
      },
      link: {
        active: DARK_BLUE,
        visited: VIOLET_LIGHT,
        hover: BLUE_DARK,
      },
      background: {
        white: WHITE,
        primary: GREY_LIGHT,
        secondary: GREY,
        tertiary: GREY_DARK,
        dark: DARK_BLUE,
      },
      custom: {
        grey_hover: {
          background: '#ECF2F8', // hover for grey light
        },
        login: {
          background: '#F8FBFF', // blue super light
        },
        slide2: {
          background: '#DFF3FF', // blue lighter
        },
        slide4: {
          background: '#FF9E0D', // orange
        },
        slide5: {
          background: '#80C6FD', // blue dim
        },
        tag: {
          background: `${BLUE}1A`, // blue super light,
          text: BLUE, // blue
        },
        button: {
          // only for button
          blue: {
            medium: '#DBEEFC',
            black: '#101825',
            dark: '#1B94EE',
            darker: '#0C83DC',
          },
          // TODO update value with medium and darker violet
          // medium sur les filtres dans les mmebres
          primary: {
            medium: '#482882',
            darker: '#482882',
          },
        },
        input: {
          selection: BLUE,
        },
        selection: PRIMARY_COLOR,
      },
      module: {
        observation: OBSERVATION,
        form: FORM,
        document: DOCUMENT,
        file: FILE,
      },
      divider: fade(GREY_DARK, 0.2),
      icon: {
        main: GREY_DARK,
        contrast: WHITE,
        action: PRIMARY_COLOR,
      },
      ...(mode === 'dark' ? paletteDarkTheme : {}),
    },
    spacing: 4,
    typography: {
      header: {
        fontSize: FONTSIZE_XL, // 20px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '34px',
        color: DARK_BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      h2: {
        fontSize: FONTSIZE_L, // 18px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.5',
        color: DARK_BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      h3: {
        fontSize: FONTSIZE_M, // 16px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.5',
        color: DARK_BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      h4: {
        fontSize: FONTSIZE_M, // 16px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.5',
        color: GREY_DARK,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      h5: {
        fontSize: FONTSIZE_S, // 14px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.5',
        color: GREY_DARK,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      h6: {
        fontSize: FONTSIZE_S, // 14px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.5',
        color: DARK_BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      body: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_S, // 14px
        fontWeight: 'normal',
        lineHeight: '1.5',
        color: DARK_BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      bodyWhite: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_S, // 14px
        fontWeight: 'normal',
        lineHeight: '1.5',
        color: WHITE,
      },
      bodyLight: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_S, // 14px
        fontWeight: 'normal',
        lineHeight: '1.5',
        color: GREY_DARK,
        whiteSpace: 'pre-line',
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      subtitle: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_XS, // 12px
        fontWeight: 'normal',
        lineHeight: '1.2',
        color: DARK_BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      subtitleWhite: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_XS, // 12px
        fontWeight: 'normal',
        lineHeight: '1.2',
        color: WHITE,
      },
      subtitleLight: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_XS, // 12px
        fontWeight: 'normal',
        lineHeight: '1.2',
        color: GREY_DARK,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      subtitleBold: {
        fontSize: FONTSIZE_XS, // 12px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.2',
        color: DARK_BLUE,
      },
      subtitleLightBold: {
        fontSize: FONTSIZE_XS, // 12px
        fontFamily: `${MEDIUM_TYPO} !important`,
        lineHeight: '1.2',
        color: GREY_DARK,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      subtitleInfo: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_XS, // 12px
        fontWeight: 'normal',
        lineHeight: '1.2',
        color: BLUE,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      subtitleError: {
        fontSize: FONTSIZE_XS, // 12px
        fontFamily: `${MEDIUM_TYPO} !important`,
        fontWeight: 500,
        lineHeight: '1.2',
        color: RED,
      },
      hint: {
        fontSize: FONTSIZE_XS,
        fontWeight: 'normal',
        lineHeight: '1.2',
        color: GREY_DARK,
        ...(mode === 'dark' ? { color: GREY_LIGHT } : {}),
      },
      link: {
        fontFamily: CLASSIC_TYPO,
        fontSize: `${FONTSIZE_S} !important`, // 14px
        lineHeight: '1.5',
        color: BLUE,
      },
      smallLink: {
        fontFamily: CLASSIC_TYPO,
        fontSize: FONTSIZE_XS, // 12px
        lineHeight: '1.2',
      },
      fontFamily: ARRAY_TYPO.join(','),
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }) => ({
            alignItems: 'center',
            fontSize: FONTSIZE_XS,
            fontFamily: CLASSIC_TYPO,
            fontWeight: 'normal',
            lineHeight: '1.5',
            padding: theme.spacing(3, 5),
            borderRadius: 8,
            textAlign: 'left',
            '& .MuiAlert-icon': {
              padding: 0,
              marginRight: theme.spacing(4),
            },
          }),
          filledWarning: ({ theme }) => ({
            color: theme.palette.text.dark,
            backgroundColor: theme.palette.secondary.light,
            '& .MuiAlert-icon': {
              color: theme.palette.text.warning,
            },
          }),
          filledInfo: ({ theme }) => ({
            color: theme.palette.info.main,
            backgroundColor: theme.palette.info.light,
            '& .MuiAlert-icon': {
              color: theme.palette.info.main,
            },
          }),
          outlinedInfo: ({ theme }) => ({
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.text.secondary}`,
            marginBottom: theme.spacing(4),
            '& .MuiAlert-icon': {
              color: 'currentColor',
            },
          }),
        },
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: 'link' },
            style: { ...linkStyle },
          },
        ],
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
        variants: [
          {
            props: { variant: 'cell' },
            style: ({ theme }) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: theme.spacing(2),
              borderRadius: '0.75rem',
              backgroundColor: theme.palette.background.primary,
              border: `1px solid ${theme.palette.background.primary}`,
              padding: theme.spacing(1.5, 2.5),
              width: '100%',
              '&.Mui-disabled': {
                color: theme.palette.text.dark,
                opacity: '0.5',
              },
              '&:hover:not(disabled)': {
                color: theme.palette.text.dark,
                backgroundColor: theme.palette.background.primary,
                border: `1px solid ${theme.palette.background.tertiary}`,
              },
              '&:focus:not(disabled)': {
                color: theme.palette.text.dark,
                backgroundColor: theme.palette.background.primary,
                border: `1px solid ${theme.palette.background.tertiary}`,
              },
              '&:active:not(disabled)': {
                color: theme.palette.text.dark,
                backgroundColor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.background.tertiary}`,
              },
            }),
          },
        ],
      },
      MuiSwitch: {
        defaultProps: {
          color: 'secondary',
        },
        styleOverrides: {
          root: {
            '.MuiSwitch-track': {
              backgroundColor: GREY,
              opacity: 1,
            },
          },
          switchBase: {
            '&.Mui-checked': {
              color: WHITE,
              '& + .MuiSwitch-track': {
                backgroundColor: PRIMARY_COLOR,
                opacity: 1,
              },
            },
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(23, 33, 52, 0.6)',
          },
          invisible: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: DARK_BLUE,
            textDecorationColor: DARK_BLUE,
            textDecoration: 'none',
            fontWeight: 'normal',
            padding: '0 0.5em',
            '&:hover:not(:disabled)': {
              textDecoration: 'underline',
            },
            '&:focus:not(:disabled)': {
              color: DARK_BLUE,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
            color: DARK_BLUE,
            fontSize: FONTSIZE_S,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: WHITE,
            ...(mode === 'dark' ? { backgroundColor: BLACK_BLUE } : {}),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: WHITE,
            ...(mode === 'dark' ? paperDarkTheme : {}),
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: DARK_BLUE,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: WHITE,
            ...(mode === 'dark' ? { backgroundColor: DARK_BLUE } : {}),
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: FONTSIZE_S,
            color: DARK_BLUE,
            '&::placeholder': {
              color: GREY_DARK,
              opacity: 1,
              fontSize: FONTSIZE_S,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: GREY_DARK,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: FONTSIZE_XS,
            height: 32,
            color: GREY_DARK,
            borderRadius: 24,
            backgroundColor: 'transparent',
            border: `1px solid ${GREY}`,
            margin: `0 4px`,
          },
          colorSecondary: {
            border: `1px solid ${PRIMARY_COLOR}`,
          },
          deleteIcon: {
            width: 14,
            height: 14,
            borderRadius: '50%',
            marginLeft: 1,
            backgroundColor: DARK_BLUE,
            '&:hover': {
              color: GREY,
            },
            color: GREY_LIGHT,
            padding: 2,
          },
          deleteIconColorPrimary: {
            backgroundColor: GREY_LIGHT,
            color: DARK_BLUE,
            '&:hover': {
              color: GREY_DARK,
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          panelHeader: {
            display: 'none',
          },
          columnsPanel: {
            paddingBottom: '24px',
          },
          columnsManagementHeader: {
            display: 'none',
          },
          columnsManagementFooter: {
            '& button': {
              display: 'none',
            },
          },
          cell: {
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: PRIMARY_COLOR,
            },
            '&.Mui-disabled': {
              color: PRIMARY_COLOR,
              opacity: 0.3,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: DARK_BLUE,
            color: WHITE,
            fontSize: FONTSIZE_XS,
            padding: 8,
            whiteSpace: 'pre-wrap',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&:before': {
              height: 0,
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
            },
            '&.Mui-expanded': {
              minHeight: 'initial',
            },
          },
        },
      },
    },
    scrollbar: {
      small: {
        overflowY: 'auto',
        scrollbarWidth: 'thin', // only works on firefox
        '&::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          marginTop: 1,
          marginBottom: 1,
          backgroundColor: 'background.primary',
          borderRadius: 6,
        },

        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'background.secondary',
          borderRadius: 6,
        },
      },
    },
  });
export default theme;
